import { makeCardId, RANKS, SUITS, type CardId, type Rank } from './cards';

export type DeckRecipeId = 'CLASSIC_4' | 'DOUBLE_6' | 'DOUBLE_8' | 'LEGACY_6' | 'LEGACY_8';

export type RuleConfidence = 'TRADITIONAL' | 'DOCUMENTED_VARIANT' | 'HOUSE_RULE';

export interface DeckRecipe {
  readonly id: DeckRecipeId;
  readonly packs: number;
  /** Ranks removed from every pack (e.g. all 2s). */
  readonly removedRanks: readonly Rank[];
  readonly playerCount: 4 | 6 | 8;
  /** Packet sizes dealt to each player in turn; the first packet is the Band reserve packet. */
  readonly packets: readonly number[];
  readonly confidence: RuleConfidence;
}

/**
 * PRD §3. Extended (6/8 player) recipes are product-defined house rules, not universal tradition —
 * new recipes are added here as data, never as new gameplay branches.
 */
export const DECK_RECIPES: Readonly<Record<DeckRecipeId, DeckRecipe>> = {
  CLASSIC_4: {
    id: 'CLASSIC_4',
    packs: 1,
    removedRanks: [],
    playerCount: 4,
    packets: [5, 4, 4],
    confidence: 'TRADITIONAL',
  },
  DOUBLE_6: {
    id: 'DOUBLE_6',
    packs: 2,
    removedRanks: [2],
    playerCount: 6,
    packets: [5, 5, 6],
    confidence: 'HOUSE_RULE',
  },
  DOUBLE_8: {
    id: 'DOUBLE_8',
    packs: 2,
    removedRanks: [],
    playerCount: 8,
    packets: [5, 4, 4],
    confidence: 'HOUSE_RULE',
  },
  LEGACY_6: {
    id: 'LEGACY_6',
    packs: 1,
    removedRanks: [2],
    playerCount: 6,
    packets: [5, 3],
    confidence: 'DOCUMENTED_VARIANT',
  },
  LEGACY_8: {
    id: 'LEGACY_8',
    packs: 1,
    removedRanks: [2],
    playerCount: 8,
    packets: [3, 3],
    confidence: 'DOCUMENTED_VARIANT',
  },
};

/** Unshuffled deck in pack → suit → rank order. */
export function buildDeck(recipe: DeckRecipe): CardId[] {
  const deck: CardId[] = [];
  for (let pack = 0; pack < recipe.packs; pack++) {
    for (const suit of SUITS) {
      for (const rank of RANKS) {
        if (!recipe.removedRanks.includes(rank)) deck.push(makeCardId(pack, suit, rank));
      }
    }
  }
  return deck;
}

export interface RecipeStats {
  cardsInPlay: number;
  handSize: number;
  tricks: number;
  tens: number;
}

export function recipeStats(recipe: DeckRecipe): RecipeStats {
  const ranksPerSuit = RANKS.length - recipe.removedRanks.length;
  const cardsInPlay = recipe.packs * SUITS.length * ranksPerSuit;
  const handSize = cardsInPlay / recipe.playerCount;
  const tens = recipe.removedRanks.includes(10) ? 0 : recipe.packs * SUITS.length;
  return { cardsInPlay, handSize, tricks: handSize, tens };
}

export function validateRecipe(recipe: DeckRecipe): string[] {
  const errors: string[] = [];
  const stats = recipeStats(recipe);
  if (!Number.isInteger(stats.handSize)) {
    errors.push(
      `${recipe.id}: ${stats.cardsInPlay} cards do not divide among ${recipe.playerCount}`,
    );
  }
  const packetTotal = recipe.packets.reduce((sum, p) => sum + p, 0);
  if (packetTotal !== stats.handSize) {
    errors.push(`${recipe.id}: packets sum to ${packetTotal}, hand size is ${stats.handSize}`);
  }
  if (recipe.playerCount % 2 !== 0) errors.push(`${recipe.id}: player count must be even`);
  return errors;
}
